import { useMemo, useEffect, useState, useCallback, ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { format, isToday } from "date-fns";
import { ChevronUp, ChevronDown, RotateCcw, X, ArrowRightLeft, Mail, Loader2, StickyNote } from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { Table, CombinedTable, TableAvailability, TableWeekdayAvailability, Reservation } from "@/types/table";
import { calculateEffectiveCapacity } from "@/lib/childrenCapacityUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { TimelineReservationPopover } from "@/components/TimelineReservationPopover";
import { useTimelineTableOrder } from "@/hooks/useTimelineTableOrder";
import { TableSwapConfirmDialog } from "@/components/TableSwapConfirmDialog";
import { hasOvernight, isOvernightSlot, serviceSortMinutes } from "@/lib/serviceOrder";

interface DashboardReservation {
  id: number;
  supabaseId: string;
  time: string;
  name: string;
  persons: number;
  numberOfChildren: number;
  hasStroller?: boolean;
  tableId: string | undefined;
  combinedTableId: string | undefined;
  status: "planifié" | "arrivé" | "annulé" | "en_attente";
  cancellationReason?: string;
  phone: string;
  email: string | undefined;
  reservationDate: Date;
  createdAt: Date;
  notes: string | undefined;
  internalNotes: string | undefined;
  durationMinutes?: number;
  terracePreference?: string;
  source?: string;
}

interface TimeSlot {
  start: string;
  end: string;
  availableDays?: number[];
}

interface DashboardTimelineViewProps {
  reservations: DashboardReservation[];
  selectedDate: Date;
  tables: Table[];
  combinedTables: CombinedTable[];
  timeSlots: TimeSlot[];
  reservationDurationMinutes: number;
  bookingMode?: 'slot' | 'time';
  onEdit: (reservation: DashboardReservation) => void;
  onUpdateStatus: (reservationId: number, status: "arrivé" | "annulé" | "planifié", cancellationReason?: string) => void;
  onQuickTableChange: (reservationId: number, tableId: string | null, combinedTableId: string | null) => void;
  onSwapTables?: (aSupabaseId: string, bSupabaseId: string) => Promise<void>;
  onSendEmail: (data: { reservationId: number; reservationUuid: string; emailType: 'cancellation' | 'modification' | 'confirmation'; reservationName: string; reservationEmail: string }) => void;
  sendingEmail: number | null;
  getReservationStatus: (reservation: DashboardReservation) => { type: string; label: string; delay: number; variant: string };
  getLoyaltyBadge: (email: string | undefined) => ReactNode;
  getNoShowBadge: (email: string | undefined) => ReactNode;
  getCustomerNotes: (email: string | undefined) => string | null;
  childrenEnabled?: boolean;
  childrenCapacityMode?: 'full' | 'half' | 'none';
  convertedReservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  establishmentId?: string;
}

const naturalSort = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const minutesToTime = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const DashboardTimelineView = ({
  reservations,
  selectedDate,
  tables,
  combinedTables,
  timeSlots,
  reservationDurationMinutes,
  bookingMode = 'slot',
  onEdit,
  onUpdateStatus,
  onQuickTableChange,
  onSwapTables,
  onSendEmail,
  sendingEmail,
  getReservationStatus,
  getLoyaltyBadge,
  getNoShowBadge,
  getCustomerNotes,
  childrenEnabled = false,
  childrenCapacityMode = 'full',
  convertedReservations,
  availabilities,
  weekdayAvailabilities,
  establishmentId,
}: DashboardTimelineViewProps) => {
  const isMobile = useIsMobile();
  const [currentMinutes, setCurrentMinutes] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });

  // Swap state
  const [swapSource, setSwapSource] = useState<DashboardReservation | null>(null);
  const [showSwapDialog, setShowSwapDialog] = useState(false);
  const [swapDraggedReservation, setSwapDraggedReservation] = useState<{
    id: string; supabaseId: string; customerName: string; numberOfPeople: number; tableName: string; tableId: string | null; combinedTableId: string | null;
  } | null>(null);
  const [swapTargetReservation, setSwapTargetReservation] = useState<{
    id: string; supabaseId: string; customerName: string; numberOfPeople: number; tableName: string; tableId: string | null; combinedTableId: string | null;
  } | null>(null);
  const [swapSubmitting, setSwapSubmitting] = useState(false);

  const getTableName = useCallback((tableId?: string, combinedTableId?: string) => {
    if (tableId) {
      const t = tables.find(t => t.id === tableId);
      return t?.name || "Table supprimée";
    }
    if (combinedTableId) {
      const ct = combinedTables.find(c => c.id === combinedTableId);
      return ct?.name || "Table combinée supprimée";
    }
    return "Non assignée";
  }, [tables, combinedTables]);

  const handleSwapSelect = useCallback((reservation: DashboardReservation) => {
    setSwapSource(reservation);
  }, []);

  const handleBarClick = useCallback((reservation: DashboardReservation) => {
    if (!swapSource) return false;
    if (reservation.id === swapSource.id) {
      setSwapSource(null);
      return true;
    }
    if (reservation.status === "annulé" || (!reservation.tableId && !reservation.combinedTableId)) {
      return false;
    }
    setSwapDraggedReservation({
      id: String(swapSource.id),
      supabaseId: swapSource.supabaseId,
      customerName: swapSource.name,
      numberOfPeople: swapSource.persons,
      tableName: getTableName(swapSource.tableId, swapSource.combinedTableId),
      tableId: swapSource.tableId || null,
      combinedTableId: swapSource.combinedTableId || null,
    });
    setSwapTargetReservation({
      id: String(reservation.id),
      supabaseId: reservation.supabaseId,
      customerName: reservation.name,
      numberOfPeople: reservation.persons,
      tableName: getTableName(reservation.tableId, reservation.combinedTableId),
      tableId: reservation.tableId || null,
      combinedTableId: reservation.combinedTableId || null,
    });
    setShowSwapDialog(true);
    return true;
  }, [swapSource, getTableName]);

  const handleSwapConfirm = useCallback(async () => {
    if (!swapDraggedReservation || !swapTargetReservation) return;
    setSwapSubmitting(true);
    try {
      if (onSwapTables) {
        await onSwapTables(swapDraggedReservation.supabaseId, swapTargetReservation.supabaseId);
      } else {
        // Fallback (non-atomic) — kept for backward compatibility if no parent handler is provided.
        const sourceId = Number(swapDraggedReservation.id);
        const targetId = Number(swapTargetReservation.id);
        onQuickTableChange(sourceId, swapTargetReservation.tableId, swapTargetReservation.combinedTableId);
        onQuickTableChange(targetId, swapDraggedReservation.tableId, swapDraggedReservation.combinedTableId);
      }
      setShowSwapDialog(false);
      setSwapSource(null);
      setSwapDraggedReservation(null);
      setSwapTargetReservation(null);
    } catch {
      // Keep the dialog open so the user sees the error toast and can retry/cancel.
    } finally {
      setSwapSubmitting(false);
    }
  }, [swapDraggedReservation, swapTargetReservation, onSwapTables, onQuickTableChange]);

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentMinutes(now.getHours() * 60 + now.getMinutes());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Filter reservations for the selected date
  const dateReservations = useMemo(
    () =>
      reservations.filter(
        (r) =>
          format(r.reservationDate, "yyyy-MM-dd") ===
          format(selectedDate, "yyyy-MM-dd")
      ),
    [reservations, selectedDate]
  );

  const overnight = useMemo(() => hasOvernight(timeSlots), [timeSlots]);
  // Minutes de service d'une réservation (identique à timeToMinutes sans créneau de nuit)
  const resMinutes = (time: string) => (overnight ? serviceSortMinutes(time, timeSlots) : timeToMinutes(time));

  // Calculate time range from time slots
  const { rangeStart, rangeEnd, totalMinutes } = useMemo(() => {
    if (timeSlots.length === 0) {
      return { rangeStart: 8 * 60, rangeEnd: 23 * 60, totalMinutes: 15 * 60 };
    }
    const starts = timeSlots.map((s) => timeToMinutes(s.start));
    // Créneau de nuit (fin < début) : la fin est le lendemain (+24h)
    const ends = timeSlots.map((s) => timeToMinutes(s.end) + (isOvernightSlot(s) ? 1440 : 0));
    const minStart = Math.floor(Math.min(...starts) / 60) * 60; // Round down to hour
    const maxEnd = Math.ceil(Math.max(...ends) / 60) * 60; // Round up to hour
    // Add 1h margin after last slot for overflow reservations
    const end = maxEnd + 60;
    return { rangeStart: minStart, rangeEnd: end, totalMinutes: end - minStart };
  }, [timeSlots]);

  // Build hour labels
  const hourLabels = useMemo(() => {
    const labels: string[] = [];
    for (let m = rangeStart; m < rangeEnd; m += 60) {
      labels.push(overnight ? minutesToTime(m % 1440) : minutesToTime(m));
    }
    return labels;
  }, [rangeStart, rangeEnd, overnight]);

  // Build table rows (active simple + combined, sorted naturally)
  const defaultTableRows = useMemo(() => {
    const simpleTables = tables
      .filter((t) => t.isActive)
      .map((t) => ({ id: t.id, name: t.name, type: "table" as const }));

    const combined = combinedTables
      .filter((ct) => ct.isActive)
      .map((ct) => ({
        id: ct.id,
        name: ct.name,
        type: "combined" as const,
      }));

    return [...simpleTables, ...combined].sort((a, b) => naturalSort(a.name, b.name));
  }, [tables, combinedTables]);

  const { sortedRows: tableRows, hasCustomOrder, isDbOrder, hasLocalOnly, isSyncing, moveRow, resetOrder, syncLocalToDb } = useTimelineTableOrder(defaultTableRows, establishmentId, tables, combinedTables);

  // Map reservations to their row
  const reservationsByRow = useMemo(() => {
    const map = new Map<string, DashboardReservation[]>();
    // Initialize all rows
    for (const row of tableRows) {
      map.set(row.id, []);
    }
    map.set("__unassigned__", []);

    for (const r of dateReservations) {
      const key = r.tableId || r.combinedTableId || "__unassigned__";
      const arr = map.get(key);
      if (arr) {
        arr.push(r);
      } else {
        // Table not in active list - put in unassigned
        map.get("__unassigned__")!.push(r);
      }
    }
    return map;
  }, [dateReservations, tableRows]);

  // Calculate effective duration for a reservation (slot mode uses slot duration)
  const getEffectiveDuration = (r: DashboardReservation): number => {
    if (r.durationMinutes) return r.durationMinutes;

    if (bookingMode === 'slot') {
      const dayOfWeek = selectedDate.getDay();
      const matchingSlot = timeSlots.find(ts =>
        ts.start.substring(0, 5) === r.time &&
        (!ts.availableDays || ts.availableDays.length === 0 || ts.availableDays.includes(dayOfWeek))
      );
      if (matchingSlot) {
        return timeToMinutes(matchingSlot.end) + (isOvernightSlot(matchingSlot) ? 1440 : 0) - timeToMinutes(matchingSlot.start);
      }
    }

    return reservationDurationMinutes || 120;
  };

  const hasUnassigned = (reservationsByRow.get("__unassigned__")?.length ?? 0) > 0;

  const allRows = useMemo(() => {
    const rows = [...tableRows];
    if (hasUnassigned) {
      rows.push({ id: "__unassigned__", name: "Non assignée", type: "table" as const });
    }
    return rows;
  }, [tableRows, hasUnassigned]);

  // Ghost blocks: cross-unavailability between combined and individual tables
  const ghostBlocksByRow = useMemo(() => {
    const map = new Map<string, Array<{ leftPercent: number; widthPercent: number; label: string }>>();

    for (const row of allRows) {
      const ghosts: Array<{ leftPercent: number; widthPercent: number; label: string }> = [];

      if (row.type === "combined") {
        const ct = combinedTables.find(c => c.id === row.id);
        if (ct) {
          for (const subTableId of ct.tableIds) {
            const subReservations = reservationsByRow.get(subTableId) || [];
            for (const r of subReservations) {
              if (r.status === "annulé") continue;
              const startMin = resMinutes(r.time);
              const duration = getEffectiveDuration(r);
              ghosts.push({
                leftPercent: ((startMin - rangeStart) / totalMinutes) * 100,
                widthPercent: (duration / totalMinutes) * 100,
                label: getTableName(subTableId),
              });
            }
          }
        }
      } else if (row.id !== "__unassigned__") {
        const parentCombos = combinedTables.filter(ct => ct.tableIds.includes(row.id));
        for (const combo of parentCombos) {
          const comboReservations = reservationsByRow.get(combo.id) || [];
          for (const r of comboReservations) {
            if (r.status === "annulé") continue;
            const startMin = resMinutes(r.time);
            const duration = getEffectiveDuration(r);
            ghosts.push({
              leftPercent: ((startMin - rangeStart) / totalMinutes) * 100,
              widthPercent: (duration / totalMinutes) * 100,
              label: combo.name,
            });
          }
        }
      }

      map.set(row.id, ghosts);
    }

    return map;
  }, [allRows, combinedTables, reservationsByRow, rangeStart, totalMinutes, getTableName]);

  if (dateReservations.length === 0) {
    return (
      <Card className="p-8 text-center text-muted-foreground">
        <p className="text-sm">Aucune réservation pour cette date</p>
      </Card>
    );
  }

  const nowServiceMinutes = overnight && currentMinutes < rangeStart ? currentMinutes + 1440 : currentMinutes;
  const showNowLine = isToday(selectedDate) && nowServiceMinutes >= rangeStart && nowServiceMinutes <= rangeEnd;
  const nowLeftPercent = ((nowServiceMinutes - rangeStart) / totalMinutes) * 100;

  const tableColumnWidth = isMobile ? 90 : 180;
  const minTimelineWidth = hourLabels.length * (isMobile ? 80 : 120);

  const getBarColor = (status: string, cancellationReason?: string) => {
    switch (status) {
      case "arrivé":
        return "bg-green-500/80 hover:bg-green-500/90 border-green-600/30";
      case "annulé":
        if (cancellationReason === 'no-show') {
          return "bg-gray-600/50 hover:bg-gray-600/60 border-gray-700/30";
        }
        return "bg-slate-400/30 hover:bg-slate-400/40 border-slate-400/20";
      default:
        return "bg-primary/80 hover:bg-primary/90 border-primary/30";
    }
  };

  return (
    <>
      {/* Swap mode banner */}
      {swapSource && (
        <div className="flex items-center gap-2 px-3 py-2 mb-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-sm text-orange-700 dark:text-orange-300">
          <ArrowRightLeft className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">
            Cliquez sur une autre réservation pour échanger la table de <strong>{swapSource.name}</strong>
          </span>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSwapSource(null)}>
            <X className="h-3 w-3 mr-1" /> Annuler
          </Button>
        </div>
      )}
      <Card className="overflow-hidden">
      <div className="flex">
        {/* Table names column (sticky left) */}
        <div
          className="flex-shrink-0 border-r bg-muted/30 z-10"
          style={{ width: tableColumnWidth }}
        >
          {/* Header */}
          <div className="h-8 border-b flex items-center px-2 justify-between gap-1">
            <span className="text-xs font-medium text-muted-foreground truncate">
              Tables{isDbOrder ? " · partagé" : hasLocalOnly ? " · local" : ""}
            </span>
            <div className="flex items-center gap-1">
              {hasLocalOnly && (
                <button
                  onClick={syncLocalToDb}
                  disabled={isSyncing}
                  className="text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-40"
                  title="Synchroniser cet ordre pour toute l'équipe"
                >
                  {isSyncing ? "..." : "Partager"}
                </button>
              )}
              {hasCustomOrder && (
                <button
                  onClick={resetOrder}
                  className="text-muted-foreground hover:text-foreground"
                  title="Réinitialiser l'ordre"
                >
                  <RotateCcw className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          {/* Rows */}
          {allRows.map((row, rowIndex) => {
            const isUnassigned = row.id === "__unassigned__";
            const isFirst = rowIndex === 0;
            const isLast = isUnassigned || rowIndex === allRows.length - 1 - (hasUnassigned ? 1 : 0);
            return (
              <div
                key={row.id}
                className="h-12 border-b flex items-center px-1 group"
              >
                {!isUnassigned && (
                  <div className="flex flex-col mr-0.5 opacity-0 group-hover:opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                    <button
                      onClick={() => moveRow(rowIndex, "up")}
                      disabled={isFirst}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-default"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => moveRow(rowIndex, "down")}
                      disabled={isLast}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-20 disabled:cursor-default"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                )}
                <span
                  className={cn(
                    "text-xs font-medium truncate",
                    isUnassigned && "text-muted-foreground italic"
                  )}
                  title={row.name}
                >
                  {row.name}
                  {row.type === 'table' && tables.find(t => t.id === row.id)?.isTerrace && ' ☀️'}
                  {row.type === 'combined' && combinedTables.find(c => c.id === row.id)?.isTerrace && ' ☀️'}
                </span>
              </div>
            );
          })}
        </div>

        {/* Timeline area (scrollable) */}
        <div className="flex-1 overflow-x-auto">
          <div style={{ minWidth: minTimelineWidth }} className="relative">
            {/* Hour headers */}
            <div className="h-8 border-b flex relative">
              {hourLabels.map((label, i) => (
                <div
                  key={label}
                  className="flex-1 text-xs text-muted-foreground flex items-center justify-start pl-1 border-l border-border/50"
                >
                  {label}
                </div>
              ))}
            </div>

            {/* Rows */}
            {allRows.map((row) => {
              const rowReservations = reservationsByRow.get(row.id) || [];
              return (
                <div
                  key={row.id}
                  className="h-12 border-b relative"
                >
                  {/* Grid lines */}
                  {hourLabels.map((label, labelIdx) => (
                    <div
                      key={label}
                      className="absolute top-0 bottom-0 border-l border-border/20"
                      style={{
                        left: `${((labelIdx * 60) / totalMinutes) * 100}%`,
                      }}
                    />
                  ))}

                  {/* Ghost blocks - cross-unavailability */}
                  {(ghostBlocksByRow.get(row.id) || []).map((ghost, i) => (
                    <div
                      key={`ghost-${i}`}
                      className="absolute top-1 bottom-1 rounded-md border border-dashed border-muted-foreground/30 pointer-events-none z-[5]"
                      style={{
                        left: `${Math.max(0, ghost.leftPercent)}%`,
                        width: `${Math.max(2, ghost.widthPercent)}%`,
                        background: `repeating-linear-gradient(-45deg, transparent, transparent 3px, hsl(var(--muted-foreground) / 0.12) 3px, hsl(var(--muted-foreground) / 0.12) 6px)`,
                      }}
                      title={`Bloquée par ${ghost.label}`}
                    />
                  ))}

                  {/* Reservation bars */}
                  {rowReservations.map((r) => {
                    const startMin = resMinutes(r.time);
                    const duration = getEffectiveDuration(r);
                    const leftPercent =
                      ((startMin - rangeStart) / totalMinutes) * 100;
                    const widthPercent = (duration / totalMinutes) * 100;
                    const endTimeStr = minutesToTime((startMin + duration) % (overnight ? 1440 : Infinity));

                    const isSwapSource = swapSource?.id === r.id;
                    const isSwapCandidate = swapSource && !isSwapSource && r.status !== "annulé" && (r.tableId || r.combinedTableId);

                    const showQuickMail = !isMobile && r.notes && r.email && r.status !== "annulé";
                    const isSendingThis = sendingEmail === r.id;
                    const quickMailType = r.status === "planifié" ? "confirmation" as const : "cancellation" as const;

                    const isCancelled = r.status === "annulé";

                    const barContent = (
                      <button
                        onClick={(e) => {
                          if (swapSource) {
                            e.stopPropagation();
                            e.preventDefault();
                            handleBarClick(r);
                          }
                        }}
                        className={cn(
                          "absolute rounded-md border text-white leading-tight px-1.5 flex items-center gap-1 cursor-pointer transition-colors overflow-hidden whitespace-nowrap",
                          isCancelled
                            ? (r.cancellationReason === 'no-show' 
                              ? "bottom-0.5 h-1.5 z-0 rounded-sm bg-gray-600/50 border-gray-700/20 text-[8px] opacity-50"
                              : "bottom-0.5 h-1.5 z-0 rounded-sm bg-slate-400/40 border-slate-400/20 text-[8px] opacity-50")
                            : "top-1 bottom-1 z-10 text-[10px]",
                          !isCancelled && getBarColor(r.status, r.cancellationReason),
                          isSwapSource && "!border-orange-500 !border-2 ring-2 ring-orange-500/40",
                          isSwapCandidate && "ring-1 ring-orange-300/50"
                        )}
                        style={{
                          left: `${Math.max(0, leftPercent)}%`,
                          width: `${Math.max(2, widthPercent)}%`,
                        }}
                      >
                       <span className="truncate font-medium">
                         {r.source === 'walk_in' && '🚶 '}
                         {r.name}
                         {!isCancelled && !isMobile && ` ${calculateEffectiveCapacity(r.persons, r.numberOfChildren, childrenCapacityMode)}p`}
                         {!isCancelled && !isMobile && (r.terracePreference === 'terrace' || (r.tableId && tables.find(t => t.id === r.tableId)?.isTerrace) || (r.combinedTableId && combinedTables.find(c => c.id === r.combinedTableId)?.isTerrace)) && ' ☀️'}
                       </span>
                        {!isCancelled && (
                          <span className={cn(
                            "flex items-center gap-0.5 flex-shrink-0",
                            (r.hasStroller || r.notes || r.internalNotes) && "bg-white/25 rounded-sm px-1 py-[1px]"
                          )}>
                            {childrenEnabled && r.numberOfChildren > 0 && (
                              <span className="text-[10px] leading-none" title={`${r.numberOfChildren} enfant(s)`}>👶{!isMobile && r.numberOfChildren}</span>
                            )}
                            {r.hasStroller && (
                              <StrollerIcon size={isMobile ? 12 : 11} aria-label="Poussette" />
                            )}
                            {(r.notes || r.internalNotes) && (
                              <StickyNote className={isMobile ? "w-3 h-3" : "w-2.5 h-2.5"} aria-label="Note" />
                            )}
                          </span>
                        )}
                       {!isCancelled && showQuickMail && (
                         <span
                           role="button"
                           onClick={(e) => {
                             e.stopPropagation();
                             e.preventDefault();
                             if (isSendingThis) return;
                             onSendEmail({
                               reservationId: r.id,
                               reservationUuid: r.supabaseId,
                               emailType: quickMailType,
                               reservationName: r.name,
                               reservationEmail: r.email!,
                             });
                           }}
                           className="flex-shrink-0 hover:scale-125 transition-transform"
                           title={`Envoyer email de ${quickMailType === 'confirmation' ? 'confirmation' : 'annulation'}`}
                         >
                           {isSendingThis ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                         </span>
                       )}
                      </button>
                    );

                    // When swap mode is active, don't wrap in popover - direct click
                    if (swapSource && !isSwapSource) {
                      return <div key={r.id}>{barContent}</div>;
                    }

                    return (
                      <TimelineReservationPopover
                        key={r.id}
                        reservation={r}
                        endTime={endTimeStr}
                        tables={tables}
                        combinedTables={combinedTables}
                        convertedReservations={convertedReservations}
                        availabilities={availabilities}
                        weekdayAvailabilities={weekdayAvailabilities}
                        getReservationStatus={getReservationStatus}
                        getLoyaltyBadge={getLoyaltyBadge}
                        getNoShowBadge={getNoShowBadge}
                        getCustomerNotes={getCustomerNotes}
                        onUpdateStatus={onUpdateStatus}
                        onEdit={onEdit}
                        onQuickTableChange={onQuickTableChange}
                        onSendEmail={onSendEmail}
                        sendingEmail={sendingEmail}
                        onSwapSelect={handleSwapSelect}
                        onSwapTables={onSwapTables}

                        reservationSettings={{
                          bookingMode,
                          reservationDurationMinutes,
                        }}
                      >
                        {barContent}
                      </TimelineReservationPopover>
                    );
                  })}
                </div>
              );
            })}

            {/* Now line */}
            {showNowLine && (
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 pointer-events-none"
                style={{ left: `${nowLeftPercent}%` }}
              >
                <div className="absolute -top-0 -left-1 w-2.5 h-2.5 bg-red-500 rounded-full" />
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
    <TableSwapConfirmDialog
      open={showSwapDialog}
      onOpenChange={(open) => {
        setShowSwapDialog(open);
        if (!open) {
          setSwapSource(null);
          setSwapDraggedReservation(null);
          setSwapTargetReservation(null);
        }
      }}
      draggedReservation={swapDraggedReservation}
      targetReservation={swapTargetReservation}
      onConfirm={handleSwapConfirm}
    />
    </>
  );
};
